import { useState, useCallback, useEffect } from 'react';
import useDebounce from 'hooks/use-debounce';
import { fetchFromGraphql } from 'entities/helpers/gqlCall';
import proposalQueries from 'apps/proposals/proposal-queries';
import ErrorLogger from 'entities/helpers/error-log-helper';

/**
 * Custom hook for searching proposals by contact IDs with title search.
 *
 * @param {number|number[]} contactIds - The contact ID(s) to search proposals for
 * @param {number} debounceMs - Debounce delay in milliseconds (default: 300)
 * @param {number} limit - Maximum number of results (default: 10)
 * @returns {object} Search state and handlers: {proposals, loading, query, error, handleSearch, clearSearch}
 */
export const useProposalsSearch = (contactIds, debounceMs = 300, limit = 10) => {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [error, setError] = useState(null);

  // Use the custom useDebounce hook for debounced query
  const debouncedQuery = useDebounce(query, debounceMs);

  // Perform search when debounced query changes or contactIds changes
  useEffect(() => {
    const performSearch = async (titleSearch) => {
      // Normalize contactIds to array
      const contactIdsArray = Array.isArray(contactIds) ? contactIds : [contactIds];

      if (!contactIds || contactIdsArray.length === 0 || contactIdsArray.every((id) => !id)) {
        setProposals([]);
        return;
      }

      // Filter out null/undefined values
      const validContactIds = contactIdsArray.filter((id) => id != null);

      if (validContactIds.length === 0) {
        setProposals([]);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const variables = {
          contact_ids: validContactIds,
          title_search: titleSearch || null,
          limit: limit,
        };

        // CURRENTLY USING LOCAL API FOR PROPOSALS SEARCH
        const response = await fetchFromGraphql(
          proposalQueries.searchProposalsByContactId,
          variables,
          'searchProposalsByContactId'
        );

        if (response?.data?.searchProposalsByContactId) {
          const results = response.data.searchProposalsByContactId.map((p) => ({ ...p, value: p.id }));
          setProposals(results);
        } else if (response?.searchProposalsByContactId) {
          // Handle case where response structure might be different
          const results = response.searchProposalsByContactId.map((p) => ({ ...p, value: p.id }));
          setProposals(results);
        } else {
          setProposals([]);
        }
      } catch (error) {
        ErrorLogger.error('Error searching proposals:', error);
        setProposals([]);
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    performSearch(debouncedQuery);
    // Use JSON.stringify to compare array values, not reference
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQuery, JSON.stringify(contactIds), limit]);

  const handleSearch = useCallback((newQuery) => {
    setQuery(newQuery);
  }, []);

  const clearSearch = useCallback(() => {
    setQuery('');
  }, []);

  return {
    proposals,
    loading,
    query,
    error,
    handleSearch,
    clearSearch,
  };
};

export default useProposalsSearch;
